import { useCallback, useEffect, useRef } from "react"

/** Die Felder, die der Nutzer am Plan ändert. Alles andere schickt der
 *  Browser gar nicht erst — der Server verwirft es ohnehin. */
const SPEICHERBAR = [
  "scenario", "handling_class", "contact", "project",
  "slot", "slot_secondary", "slot_tertiary",
  "reply", "monteur_task", "step_enabled", "actions",
  "customer_confirmed_slot", "slot_locked",
] as const

/**
 * Hält fest, was der Nutzer am Plan umstellt (#40).
 *
 * Bis zum 26.08.2026 lebten diese Änderungen nur im Query-Cache
 * (`qc.setQueryData`). Der Plan-Endpunkt rechnet bei jedem Laden neu, und die
 * Query ist nach fünf Minuten alt — ein Refetch, für den ein Fensterfokus
 * reichte, stellte den Serverstand wieder her. Wer einen Schritt abwählte,
 * dann telefonierte und danach bestätigte, führte womöglich aus, was er
 * ausdrücklich nicht wollte. Ohne jede Meldung: die Karte zeigte den
 * zurückgesetzten Stand, und der sieht genauso normal aus wie der eigene.
 *
 * 🔑 Gedrosselt, nicht bei jedem Tastendruck: der Plan hängt an Feldern, in
 * die der Nutzer schreibt (Titel der Aufgabe, Antworttext). Ungedrosselt wäre
 * das eine Anfrage je Zeichen.
 *
 * 🔴 `done` wird nicht mitgeschickt und serverseitig nochmals verworfen. Es ist
 * kein Zustand des Plans, sondern eine Ableitung aus dem Ist-Zustand der Mail —
 * wer es festschreibt, sperrt eine Aktion für immer (#36).
 */
export function usePlanSpeichern(emailId: number, verzoegerung = 600) {
  const offen = useRef<Record<string, unknown> | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const senden = useCallback(() => {
    const daten = offen.current
    offen.current = null
    if (!daten) return
    fetch(`/api/v1/drafts/${emailId}/plan`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(daten),
    }).catch(() => {
      // Ein fehlgeschlagenes Speichern darf die Oberfläche nicht stören —
      // der Cache trägt die Änderung ohnehin bis zum nächsten Laden.
    })
  }, [emailId])

  // Was noch aussteht, beim Verlassen nachreichen. Sonst geht der letzte
  // Stand verloren, wenn jemand die Mail innerhalb der Drosselzeit schliesst.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    senden()
  }, [senden])

  return useCallback((plan: Record<string, unknown> | null | undefined) => {
    if (!plan) return
    const daten: Record<string, unknown> = {}
    for (const feld of SPEICHERBAR) {
      if (feld in plan) daten[feld] = plan[feld]
    }
    if (Array.isArray(daten.actions)) {
      daten.actions = (daten.actions as Record<string, unknown>[])
        .filter((a) => a && a.type)
        .map(({ done: _done, ...rest }) => rest)
    }
    offen.current = daten
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(senden, verzoegerung)
  }, [senden, verzoegerung])
}
